import { Divider, ScrollShadow } from '@heroui/react';
import { manrope } from '@trwl/app/fonts';
import {
  Product,
  SearchHitSearchProductEntity,
  SearchProductEntity,
} from '@trwl/backend.api';
import { Path } from '@trwl/shared/lib/router';
import { setDuration, setShow } from '@trwl/store';
import { capitalizeText, formatLikes } from '@trwl/util';
import { preprocessImage } from '@trwl/util/preprocessImage';
import { AddToCart } from '@trwl/widgets/product/productInfo/components/AddToCart';
import { VariantSelector } from '@trwl/widgets/product/productInfo/components/VariantSelector';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { RefObject, useCallback } from 'react';

interface IProductInfo {
  likesCount?: number;
  product: Product;
  slug: string;
  sizeGridRef?: RefObject<HTMLDivElement | null>;
  relationProducts?: SearchHitSearchProductEntity[];
}
const IMAGE_SIZE = 200;
export const ProductInfo = ({
  likesCount = 0,
  product,
  sizeGridRef,
  relationProducts,
}: IProductInfo) => {
  const [changeShow, changeDuration] = useUnit([setShow, setDuration]);
  const router = useRouter();

  const onSizeGridClick = useCallback(() => {
    if (!sizeGridRef || !sizeGridRef.current) return;
    sizeGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [sizeGridRef]);

  const onProductClick = useCallback(
    (productId: number) => {
      changeDuration(20000);
      changeShow(true);
      router.push(`${Path.PRODUCT}/${productId}`);
    },
    [router, changeShow, changeDuration],
  );

  return (
    <div className="flex flex-col w-full col-span-5 lg:col-span-2">
      {likesCount > 0 && (
        <div
          className={cn(
            'flex flex-row gap-[6px] text-[12px] font-[500] items-center mb-[10px]',
            manrope.className,
          )}
        >
          <span>Товар добавили в вишлист</span>
          <span className="font-[700]">{formatLikes(likesCount)}</span>
          <span>раз</span>
        </div>
      )}

      <div className="flex flex-col gap-[8px]">
        <h1 className="text-[#282828] text-[28px] font-[500] m-0">
          {capitalizeText(product.title)}
        </h1>
      </div>
      {relationProducts && relationProducts.length > 0 && (
        <ScrollShadow
          className={'max-[662px]:block hidden mt-[10px]'}
          size={0}
          orientation={'horizontal'}
          hideScrollBar
        >
          <div className="flex flex-col max-[662px]:flex-row gap-[8px]">
            <div
              key={'current_product'}
              className={
                'w-[65px] h-[65px] min-h-[65px] min-w-[65px] max-w-[65px] max-h-[65px] transition rounded-[4px] cursor-pointer border-[1px] border-gray-300'
              }
            >
              <Image
                className={'p-0.5'}
                src={preprocessImage(product.logoUrl, IMAGE_SIZE)}
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                alt={product.title}
              />
            </div>
            {relationProducts
              .filter((t) => !!t.content)
              .map((t) => t.content as NonNullable<SearchProductEntity>)
              .filter((productEntity) => productEntity.id !== product.id)
              .map((productEntity, key) => (
                <div
                  key={key}
                  className={
                    'w-[65px] h-[65px] min-h-[65px] min-w-[65px] max-w-[65px] max-h-[65px] transition rounded-[4px] cursor-pointer'
                  }
                  onClick={() => onProductClick(productEntity.id)}
                >
                  <Image
                    className={'p-0.5'}
                    src={preprocessImage(productEntity?.logo ?? '', IMAGE_SIZE)}
                    width={IMAGE_SIZE}
                    height={IMAGE_SIZE}
                    alt={productEntity?.title}
                  />
                </div>
              ))}
          </div>
        </ScrollShadow>
      )}

      <div className="flex flex-col gap-[10px] mt-[20px]">
        <VariantSelector product={product} onSizeGridClick={onSizeGridClick} />
        <Divider className={'mt-[25px]'} />
        <AddToCart product={product} />
      </div>
    </div>
  );
};
